import { Controller } from "@hotwired/stimulus"
import {
  recent_notifications_path,
  mark_as_seen_notification_path,
  mark_as_read_notification_path,
  mark_multiple_as_seen_notifications_path,
  mark_multiple_as_read_notifications_path,
  mark_all_as_read_notifications_path,
  unread_count_notifications_path
} from "../routes"


export default class extends Controller {
  static targets = ["icon", "bellIcon", "badge", "dropdown", "content"]
  static values = { personId: String }

  async loadRecentNotifications() {
    try {
      const response = await fetch(recent_notifications_path())
      const html = await response.text()
      this.contentTarget.innerHTML = html
    } catch (error) {
      console.error('Error loading recent notifications:', error)
    }
  }

  updateBadge(count) {
    if (count > 0) {
      this.badgeTarget.textContent = count
      this.badgeTarget.classList.remove('d-none')
    } else {
      this.badgeTarget.classList.add('d-none')
    }
  }

  updatePulsingAnimation(count) {
    if (count > 0) {
      this.iconTarget.classList.add('has-unread')
    } else {
      this.iconTarget.classList.remove('has-unread')
    }
  }

  startNotificationAnimation() {
    this.iconTarget.classList.add('has-new-notification')
  }

  stopNotificationAnimation() {
    this.iconTarget.classList.remove('has-new-notification')
  }

  async markSingleNotificationAsSeen(notificationId) {
    try {
      await fetch(mark_as_seen_notification_path(notificationId), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })
    } catch (error) {
      console.error('Error marking notification as seen:', error)
    }
  }

  async markSingleNotificationAsRead(notificationId) {
    try {
      await fetch(mark_as_read_notification_path(notificationId), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  }

  async markMultipleNotificationsAsSeen(notificationIds) {
    try {
      await fetch(mark_multiple_as_seen_notifications_path(), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          notification_ids: notificationIds
        })
      })
    } catch (error) {
      console.error('Error marking multiple notifications as seen:', error)
    }
  }

  async markMultipleNotificationsAsRead(notificationIds) {
    try {
      await fetch(mark_multiple_as_read_notifications_path(), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          notification_ids: notificationIds
        })
      })
    } catch (error) {
      console.error('Error marking multiple notifications as read:', error)
    }
  }

  async markAllNotificationsAsRead() {
    try {
      await fetch(mark_all_as_read_notifications_path(), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      this.updateBadge(0)
      this.updatePulsingAnimation(0)
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
    }
  }

  async markAsRead(notificationId = null) {
    if (notificationId) {
      await this.markSingleNotificationAsRead(notificationId)
    }
  }

  async markAsReadFromButton(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = event.currentTarget
    const notificationId = button.dataset.notificationId
    if (!notificationId) return

    button.disabled = true

    try {
      const response = await fetch(mark_as_read_notification_path(notificationId), {
        method: 'PATCH',
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
        await this.updateUnreadCount()
      }
    } catch (error) {
      console.error('Error marking notification as read:', error)
      button.disabled = false
    }
  }

  async handleNotificationClick(event) {
    event.stopPropagation()

    const notificationElement = event.currentTarget
    const notificationId = notificationElement.dataset.notificationId
    const notifiableType = notificationElement.dataset.notifiableType
    const notifiableId = notificationElement.dataset.notifiableId
    const url = notificationElement.href

    if (notificationId) {
      try {
        await this.markSingleNotificationAsRead(notificationId)

        if (notifiableType && notifiableId) {
          await this.markNotifiableAsRead(notifiableType, notifiableId)

          const groupKey = `${notifiableType}-${notifiableId}`
          const collapseElement = document.getElementById(`collapse-${groupKey}`)
          if (collapseElement) {
            collapseElement.querySelectorAll('.list-group-item').forEach(item => {
              item.classList.remove('bg-light')
              const badge = item.querySelector('.notification-badge')
              if (badge) badge.remove()
            })
          }

          const groupHeaders = document.querySelectorAll(`[data-notifiable-type="${notifiableType}"][data-notifiable-id="${notifiableId}"]`)
          groupHeaders.forEach(header => {
            if (header.classList.contains('list-group-item')) {
              header.classList.remove('bg-light')
              const badge = header.querySelector('.notification-badge')
              if (badge) badge.remove()
            }
          })
        } else {
          this.updateNotificationUI(notificationElement)
        }

        await this.updateUnreadCount()
        window.location.href = url
        return false
      } catch (error) {
        console.error('Error marking notification as read:', error)
        window.location.href = url
        return false
      }
    } else {
      window.location.href = url
      return false
    }
  }

  async handleGroupClick(event) {
    event.stopPropagation()

    const groupElement = event.currentTarget
    const notifiableType = groupElement.dataset.notifiableType
    const notifiableId = groupElement.dataset.notifiableId

    if (notifiableType && notifiableId) {
      try {
        await this.markNotifiableAsRead(notifiableType, notifiableId)

        const groupKey = `${notifiableType}-${notifiableId}`
        const collapseElement = document.getElementById(`collapse-${groupKey}`)
        if (collapseElement) {
          collapseElement.querySelectorAll('.list-group-item').forEach(item => {
            item.classList.remove('bg-light')
            const badge = item.querySelector('.notification-badge')
            if (badge) badge.remove()
          })
        }

        groupElement.classList.remove('bg-light')
        const badge = groupElement.querySelector('.notification-badge')
        if (badge) badge.remove()

        await this.updateUnreadCount()
      } catch (error) {
        console.error('Error marking group as read:', error)
      }
    }
  }

  async markNotifiableAsRead(notifiableType, notifiableId) {
    try {
      const response = await fetch(mark_multiple_as_read_notifications_path(), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          notifiable_type: notifiableType,
          notifiable_id: notifiableId
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
    } catch (error) {
      console.error('Error marking notifiable as read:', error)
    }
  }

  updateNotificationUI(notificationElement) {
    const badge = notificationElement.querySelector('.notification-badge')
    if (badge) badge.remove()
    notificationElement.classList.remove('bg-light')
  }

  async updateUnreadCount() {
    try {
      const response = await fetch(unread_count_notifications_path())
      const data = await response.json()
      this.updateBadge(data.count)
      this.updatePulsingAnimation(data.count)
    } catch (error) {
      console.error('Error fetching updated unread count:', error)
    }
  }

  async show() {
    await this.loadRecentNotifications()
    this.stopNotificationAnimation()
  }
}
